import { UseCases } from "@aptus/frontend-core";
import { flatten } from "flat";
import { Routes } from "hooks/routes/models/routes";
import { useTranslation } from "react-i18next";
import { useEvent } from "utils/useEvent";
import { PageTitles } from "./pageTitles";

interface Props {
	routes: Routes;
}

interface Result {
	routePageTitles: PageTitles;

	setPageTitle: (pageTitle: string) => void;
	trySetRoutePageTitle: (path: string) => void;
}

export const usePageTitleUseCases: UseCases<Props, Result> = (props: Props) => {
	const { t } = useTranslation();

	const routePageTitles: Result["routePageTitles"] = {
		index: t("highFive"),
		login: t("pageTitles.login"),
		passwordReset: t("pageTitles.passwordReset"),
		requestPasswordReset: t("pageTitles.requestPasswordReset"),
		register: {
			index: t("pageTitles.register.index"),
			stepOne: `${t("pageTitles.register.index")} | ${t("pageTitles.register.stepOne")}`,
			stepTwo: `${t("pageTitles.register.index")} | ${t("pageTitles.register.stepTwo")}`,
			stepThree: `${t("pageTitles.register.index")} | ${t("pageTitles.register.stepThree")}`,
			stepFour: `${t("pageTitles.register.index")} | ${t("pageTitles.register.stepFour")}`,
			stepFive: `${t("pageTitles.register.index")} | ${t("pageTitles.register.stepFive")}`,
			stepSix: `${t("pageTitles.register.index")} | ${t("pageTitles.register.stepSix")}`,
			stepSeven: `${t("pageTitles.register.index")} | ${t("pageTitles.register.stepSeven")}`,
			done: `${t("pageTitles.register.index")} | ${t("pageTitles.register.done")}`,
		},
		forgotTagId: t("pageTitles.forgotTagId"),
	};

	const setPageTitle: Result["setPageTitle"] = (pageTitle: string) => {
		document.title = pageTitle;
	};

	const getPathKey: (path: string) => string = (path) => {
		const { routes } = props;
		const flattenedRoutes: Record<string, string> = flatten(routes);

		const matchKeys = Object.keys(flattenedRoutes).filter((key) => {
			const value = flattenedRoutes[key];

			if (value === path) {
				return true;
			}

			return false;
		});

		if (matchKeys && matchKeys.length) {
			return matchKeys[0];
		}

		return "";
	};

	const trySetRoutePageTitle: Result["trySetRoutePageTitle"] = (path: string) => {
		const pathKey = getPathKey(path);

		if (!pathKey) {
			setPageTitle(routePageTitles.index);
			return;
		}

		const title = pathKey
			.split(/[.[\]]/)
			.filter(Boolean)
			.reduce((value, key) => (value as any)?.[key], routePageTitles as any);

		if (title) {
			setPageTitle(title);
			return;
		}

		setPageTitle(routePageTitles.index);
	};

	return {
		routePageTitles,

		setPageTitle,
		trySetRoutePageTitle,
	};
};

export const usePageTitleEvents = (pageTitles: ReturnType<typeof usePageTitleUseCases>) => {
	useEvent("routeChanged", pageTitles.trySetRoutePageTitle);
};
